import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Card from '@/components/shared/Card';
import Button from '@/components/shared/Button';
import Modal from '@/components/shared/Modal';
import { apiFetch } from '@/lib/api';
import { formatDate } from '@/utils/formatters';
import { ladeNetzentgelte } from '@/data/netzentgelte';
import {
  Network, Plus, RefreshCw, Search, AlertCircle, X, Upload, FileSpreadsheet,
  Trash2, Pencil, Info, Copy, Check, CircleCheck, Gauge,
} from 'lucide-react';

/**
 * Netzentgelte — Pflege der echten Preisblaetter der Netzbetreiber.
 *
 * Quelle der Wahrheit ist der Server (/api/netzentgelte). Kein localStorage.
 * Ohne hinterlegtes Preisblatt rechnet die Kalkulation mit einer regionalen
 * Schaetzung nach Postleitzahl — das ist auf dieser Seite bewusst benannt,
 * damit niemand eine Schaetzung fuer einen echten Tarif haelt.
 *
 * Fehler werden strikt getrennt:
 *   loadError  -> Liste ist nicht da, Kennzahlen zeigen "–" (nie "0")
 *   writeError -> Banner ueber der weiterhin gueltigen Liste
 * Erfolg wird erst gemeldet, wenn der Server mit resp.ok geantwortet hat.
 */

// ---------------------------------------------------------------------------
// Konstanten
// ---------------------------------------------------------------------------

const SPANNUNGSEBENEN = [
  { wert: 'NS', label: 'NS — Niederspannung' },
  { wert: 'MS', label: 'MS — Mittelspannung' },
  { wert: 'HS', label: 'HS — Hochspannung' },
];

/**
 * Die CSV-Spalten entsprechen 1:1 den Feldern des Preisblatts.
 * `aliase` faengt die ueblichen Schreibweisen aus Excel-Exporten ab.
 */
const CSV_SPALTEN = [
  {
    key: 'netzbetreiber', typ: 'text', pflicht: true,
    label: 'Netzbetreiber', einheit: '', hinweis: 'Name exakt wie im Preisblatt',
    aliase: ['netzbetreiber', 'vnb', 'netz', 'name'],
  },
  {
    key: 'spannungsebene', typ: 'ebene',
    label: 'Spannungsebene', einheit: '', hinweis: 'NS, MS oder HS — leer wird als NS gelesen',
    aliase: ['spannungsebene', 'spannung', 'ebene', 'netzebene'],
  },
  {
    key: 'gueltig_ab', typ: 'datum', pflicht: true,
    label: 'Gueltig ab', einheit: '', hinweis: 'JJJJ-MM-TT oder TT.MM.JJJJ',
    aliase: ['gueltig_ab', 'gueltigab', 'gueltig_von', 'von', 'ab'],
  },
  {
    key: 'gueltig_bis', typ: 'datum',
    label: 'Gueltig bis', einheit: '', hinweis: 'leer = unbefristet',
    aliase: ['gueltig_bis', 'gueltigbis', 'bis'],
  },
  {
    key: 'arbeitspreis_ct_kwh', typ: 'zahl',
    label: 'SLP-Arbeitspreis', einheit: 'ct/kWh', hinweis: 'ohne Leistungsmessung',
    aliase: ['arbeitspreis_ct_kwh', 'arbeitspreis', 'arbeitspreis_slp', 'ap_slp', 'ap'],
  },
  {
    key: 'grundpreis_eur_jahr', typ: 'zahl',
    label: 'SLP-Grundpreis', einheit: 'EUR/Jahr', hinweis: 'ohne Leistungsmessung',
    aliase: ['grundpreis_eur_jahr', 'grundpreis', 'gp'],
  },
  {
    key: 'leistungspreis_eur_kw_jahr', typ: 'zahl',
    label: 'RLM-Leistungspreis', einheit: 'EUR/kW/Jahr', hinweis: 'unter der Benutzungsstundengrenze',
    aliase: ['leistungspreis_eur_kw_jahr', 'leistungspreis', 'lp', 'lp_niedrig'],
  },
  {
    key: 'arbeitspreis_rlm_ct_kwh', typ: 'zahl',
    label: 'RLM-Arbeitspreis', einheit: 'ct/kWh', hinweis: 'unter der Benutzungsstundengrenze',
    aliase: ['arbeitspreis_rlm_ct_kwh', 'arbeitspreis_rlm', 'ap_rlm', 'ap_niedrig'],
  },
  {
    key: 'leistungspreis_hoch_eur_kw_jahr', typ: 'zahl',
    label: 'RLM-Leistungspreis hoch', einheit: 'EUR/kW/Jahr', hinweis: 'ab der Benutzungsstundengrenze',
    aliase: ['leistungspreis_hoch_eur_kw_jahr', 'leistungspreis_hoch', 'lp_hoch'],
  },
  {
    key: 'arbeitspreis_hoch_ct_kwh', typ: 'zahl',
    label: 'RLM-Arbeitspreis hoch', einheit: 'ct/kWh', hinweis: 'ab der Benutzungsstundengrenze',
    aliase: ['arbeitspreis_hoch_ct_kwh', 'arbeitspreis_hoch', 'ap_hoch'],
  },
  {
    key: 'benutzungsstunden_grenze', typ: 'ganzzahl',
    label: 'Benutzungsstundengrenze', einheit: 'h/Jahr', hinweis: 'ueblich 2500, leer = 2500',
    aliase: ['benutzungsstunden_grenze', 'benutzungsstunden', 'bs_grenze', 'grenze'],
  },
  {
    key: 'messstellenbetrieb_eur_jahr', typ: 'zahl',
    label: 'Messstellenbetrieb', einheit: 'EUR/Jahr', hinweis: 'leer = 0',
    aliase: ['messstellenbetrieb_eur_jahr', 'messstellenbetrieb', 'msb', 'messentgelt'],
  },
  {
    key: 'quelle', typ: 'text',
    label: 'Quelle', einheit: '', hinweis: 'z.B. "Preisblatt 2027, Stand 01.01.2027"',
    aliase: ['quelle', 'preisblatt', 'stand'],
  },
];

const VORLAGE_KOPFZEILE = CSV_SPALTEN.map((s) => s.key).join(';');

const LEERES_FORMULAR = {
  netzbetreiber: '',
  spannungsebene: 'NS',
  gueltig_ab: '',
  gueltig_bis: '',
  arbeitspreis_ct_kwh: '',
  grundpreis_eur_jahr: '',
  leistungspreis_eur_kw_jahr: '',
  arbeitspreis_rlm_ct_kwh: '',
  leistungspreis_hoch_eur_kw_jahr: '',
  arbeitspreis_hoch_ct_kwh: '',
  benutzungsstunden_grenze: '2500',
  messstellenbetrieb_eur_jahr: '',
  quelle: '',
};

// ---------------------------------------------------------------------------
// Kleine Helfer
// ---------------------------------------------------------------------------

const pad2 = (n) => String(n).padStart(2, '0');

const heuteISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/** Beliebigen Datumswert auf einen reinen Kalendertag (YYYY-MM-DD) bringen. */
const nurTag = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const utc = new Date(d.getTime());
  if (utc.getUTCHours() >= 12) utc.setUTCDate(utc.getUTCDate() + 1);
  return utc.toISOString().slice(0, 10);
};

/** Postgres liefert NUMERIC als String — hier zu einer Zahl oder null. */
const zuZahl = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

const fmtZahl = (n, min = 2, max = 2) => (
  n == null ? '–' : Number(n).toLocaleString('de-DE', { minimumFractionDigits: min, maximumFractionDigits: max })
);

const fmtGanz = (n) => (n == null ? '–' : Number(n).toLocaleString('de-DE'));

/** Fehlertext aus einer Server-Antwort ziehen, ohne die Antwort zu erfinden. */
const leseFehler = async (resp, fallback) => {
  try {
    const data = await resp.json();
    if (data && (data.error || data.message)) return data.error || data.message;
  } catch {
    /* Antwort war kein JSON */
  }
  return `${fallback} (HTTP ${resp.status})`;
};

const normalisiereKopf = (raw) => String(raw || '')
  .replace(/^\uFEFF/, '')
  .trim()
  .toLowerCase()
  .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const spalteFuerKopf = (kopf) => CSV_SPALTEN.find((s) => s.aliase.includes(kopf)) || null;

// ---------------------------------------------------------------------------
// CSV-Verarbeitung
// ---------------------------------------------------------------------------

/** Trennzeichen aus der Kopfzeile bestimmen (deutsche Exporte nutzen Semikolon). */
const erkenneTrenner = (text) => {
  const kopf = text.split(/\r?\n/, 1)[0] || '';
  const kandidaten = [
    { zeichen: ';', anzahl: (kopf.match(/;/g) || []).length },
    { zeichen: ',', anzahl: (kopf.match(/,/g) || []).length },
    { zeichen: '\t', anzahl: (kopf.match(/\t/g) || []).length },
  ].sort((a, b) => b.anzahl - a.anzahl);
  return kandidaten[0].anzahl > 0 ? kandidaten[0].zeichen : ';';
};

/**
 * Vollstaendiger CSV-Leser: beachtet Anfuehrungszeichen inkl. "" als Escape.
 * Jeder Satz behaelt seine urspruengliche Zeilennummer — Leerzeilen werden zwar
 * uebersprungen, verschieben aber nicht die Nummerierung in der Vorschau.
 */
const leseDatensaetze = (text, trenner) => {
  const saetze = [];
  let feld = '';
  let satz = [];
  let inQuotes = false;
  let nr = 1;
  const abschliessen = () => {
    satz.push(feld);
    saetze.push({ nr, zellen: satz });
    satz = [];
    feld = '';
    nr += 1;
  };
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { feld += '"'; i += 1; } else { inQuotes = false; }
      } else { feld += c; }
      continue;
    }
    if (c === '"') { inQuotes = true; continue; }
    if (c === trenner) { satz.push(feld); feld = ''; continue; }
    if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i += 1;
      abschliessen();
      continue;
    }
    feld += c;
  }
  abschliessen();
  return saetze.filter((s) => s.zellen.some((v) => String(v).trim() !== ''));
};

/**
 * Zahl aus einem CSV-Feld. Deutsche und englische Schreibweise werden erkannt,
 * das Ergebnis steht in der Vorschau — geraten wird nichts.
 */
const parseZahlFeld = (raw) => {
  const s = String(raw).replace(/\s/g, '').replace(/€|EUR|ct\/kWh|ct/gi, '');
  if (!s) return { wert: null };
  let n = s;
  const komma = n.lastIndexOf(',');
  const punkt = n.lastIndexOf('.');
  if (komma >= 0 && punkt >= 0) {
    // Das hintere Zeichen ist das Dezimaltrennzeichen.
    if (komma > punkt) n = n.replace(/\./g, '').replace(',', '.');
    else n = n.replace(/,/g, '');
  } else if (komma >= 0) {
    n = n.replace(',', '.');
  }
  const zahl = Number(n);
  if (!Number.isFinite(zahl)) return { fehler: `"${raw}" ist keine Zahl` };

  // "1.200" ist echt mehrdeutig: 1,2 oder 1200? Beides ist bei Netzentgelten
  // plausibel (1,2 ct/kWh vs. 1.200 EUR/kW). Wir raten NICHT — ein Faktor 1000
  // im Leistungspreis wuerde jedes RLM-Angebot unbrauchbar machen.
  if (komma < 0 && /^-?\d{1,3}\.\d{3}$/.test(s)) {
    return {
      fehler: `"${raw}" ist nicht eindeutig — meinen Sie ${s.replace('.', ',')} oder ${s.replace('.', '')}? `
        + 'Bitte mit Dezimalkomma schreiben (z.B. 1,2) oder ohne Tausenderpunkt (z.B. 1200).',
    };
  }
  return { wert: zahl };
};

const parseDatumFeld = (raw) => {
  const s = String(raw).trim();
  if (!s) return { wert: null };
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return { wert: s };
  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(s);
  if (de) return { wert: `${de[3]}-${pad2(de[2])}-${pad2(de[1])}` };
  const slash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (slash) return { fehler: `"${s}" ist nicht eindeutig (Tag/Monat?) — bitte JJJJ-MM-TT schreiben` };
  return { fehler: `"${s}" ist kein Datum (erwartet: JJJJ-MM-TT oder TT.MM.JJJJ)` };
};

/**
 * CSV-Text -> { trenner, kopf, unbekannteSpalten, fehlendePflicht, zeilen }
 * Jede Zeile traegt ihre Fehler und Warnungen selbst; nur fehlerfreie Zeilen
 * werden spaeter an den Server geschickt.
 */
const parseCsv = (rohtext) => {
  const text = String(rohtext || '').replace(/^\uFEFF/, '');
  if (!text.trim()) return null;

  const trenner = erkenneTrenner(text);
  const saetze = leseDatensaetze(text, trenner);
  if (saetze.length === 0) return null;

  const kopfRoh = saetze[0].zellen.map((h) => String(h).trim());
  const kopfKeys = kopfRoh.map((h) => {
    const spalte = spalteFuerKopf(normalisiereKopf(h));
    return spalte ? spalte.key : null;
  });

  const unbekannteSpalten = kopfRoh.filter((_, i) => kopfKeys[i] === null && kopfRoh[i] !== '');
  const erkannteKeys = kopfKeys.filter(Boolean);
  const fehlendePflicht = CSV_SPALTEN
    .filter((s) => s.pflicht && !erkannteKeys.includes(s.key))
    .map((s) => s.key);

  const zeilen = saetze.slice(1).map(({ nr, zellen }) => {
    const daten = {};
    const fehler = [];
    const warnungen = [];

    kopfKeys.forEach((key, spaltenIdx) => {
      if (!key) return;
      const spalte = CSV_SPALTEN.find((s) => s.key === key);
      const roh = zellen[spaltenIdx] === undefined ? '' : String(zellen[spaltenIdx]).trim();

      if (spalte.typ === 'text') {
        daten[key] = roh || null;
        return;
      }
      if (spalte.typ === 'ebene') {
        if (!roh) { daten[key] = null; return; }
        const ebene = roh.toUpperCase().replace(/[^A-Z_]/g, '');
        if (!SPANNUNGSEBENEN.some((e) => e.wert === ebene)) {
          fehler.push(`${spalte.label}: "${roh}" ist keine bekannte Ebene (NS, MS, HS)`);
          daten[key] = null;
          return;
        }
        daten[key] = ebene;
        return;
      }
      if (spalte.typ === 'datum') {
        const r = parseDatumFeld(roh);
        if (r.fehler) fehler.push(`${spalte.label}: ${r.fehler}`);
        daten[key] = r.wert ?? null;
        return;
      }
      const r = parseZahlFeld(roh);
      if (r.fehler) fehler.push(`${spalte.label}: ${r.fehler}`);
      if (r.warnung) warnungen.push(`${spalte.label}: ${r.warnung}`);
      if (spalte.typ === 'ganzzahl' && r.wert != null && !Number.isInteger(r.wert)) {
        fehler.push(`${spalte.label}: "${roh}" muss eine ganze Zahl sein`);
      }
      daten[key] = r.wert ?? null;
    });

    CSV_SPALTEN.filter((s) => s.pflicht).forEach((s) => {
      // Nicht doppelt melden: steht zu diesem Feld schon ein Lesefehler, ist
      // "fehlt" nur Rauschen.
      if (!daten[s.key] && !fehler.some((f) => f.startsWith(`${s.label}:`))) {
        fehler.push(`${s.label} fehlt`);
      }
    });

    const preisFelder = [
      'arbeitspreis_ct_kwh', 'grundpreis_eur_jahr', 'leistungspreis_eur_kw_jahr',
      'arbeitspreis_rlm_ct_kwh', 'leistungspreis_hoch_eur_kw_jahr', 'arbeitspreis_hoch_ct_kwh',
      'messstellenbetrieb_eur_jahr',
    ];
    if (fehler.length === 0 && preisFelder.every((f) => daten[f] == null)) {
      warnungen.push('Keine einzige Preisangabe — dieses Preisblatt aendert in der Kalkulation nichts');
    }
    if (fehler.length === 0
      && (daten.leistungspreis_eur_kw_jahr == null || daten.arbeitspreis_rlm_ct_kwh == null)) {
      warnungen.push('Ohne Leistungspreis und RLM-Arbeitspreis faellt die RLM-Berechnung auf die regionale Schaetzung zurueck');
    }

    return { csvZeile: nr, daten, fehler, warnungen };
  });

  return { trenner, kopfRoh, kopfKeys, unbekannteSpalten, fehlendePflicht, zeilen };
};

const trennerName = (t) => (t === ';' ? 'Semikolon' : t === ',' ? 'Komma' : 'Tabulator');

// ---------------------------------------------------------------------------
// Formular fuer ein einzelnes Preisblatt
// ---------------------------------------------------------------------------

const Feld = ({ label, einheit, children, hinweis }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {einheit && <span className="text-gray-400 font-normal"> ({einheit})</span>}
    </label>
    {children}
    {hinweis && <p className="text-xs text-gray-400 mt-1">{hinweis}</p>}
  </div>
);

const eingabeKlasse = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none';

const PreisblattForm = ({ initial, onSave, onCancel, saving, error }) => {
  const [form, setForm] = useState(initial || LEERES_FORMULAR);
  const [lokalerFehler, setLokalerFehler] = useState('');

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const rlmUnvollstaendig = (!form.leistungspreis_eur_kw_jahr || !form.arbeitspreis_rlm_ct_kwh)
    || (!form.leistungspreis_hoch_eur_kw_jahr || !form.arbeitspreis_hoch_ct_kwh);

  const absenden = (e) => {
    e.preventDefault();
    if (!form.netzbetreiber.trim()) { setLokalerFehler('Netzbetreiber ist ein Pflichtfeld'); return; }
    if (!form.gueltig_ab) { setLokalerFehler('Gueltig ab ist ein Pflichtfeld'); return; }
    if (form.gueltig_bis && form.gueltig_bis < form.gueltig_ab) {
      setLokalerFehler('Gueltig bis liegt vor Gueltig ab'); return;
    }
    setLokalerFehler('');

    // Regel: leere Strings duerfen nie an NUMERIC/DATE-Spalten gehen.
    const zahl = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
    onSave({
      netzbetreiber: form.netzbetreiber.trim(),
      spannungsebene: form.spannungsebene || 'NS',
      gueltig_ab: form.gueltig_ab,
      gueltig_bis: form.gueltig_bis || null,
      arbeitspreis_ct_kwh: zahl(form.arbeitspreis_ct_kwh),
      grundpreis_eur_jahr: zahl(form.grundpreis_eur_jahr),
      leistungspreis_eur_kw_jahr: zahl(form.leistungspreis_eur_kw_jahr),
      arbeitspreis_rlm_ct_kwh: zahl(form.arbeitspreis_rlm_ct_kwh),
      leistungspreis_hoch_eur_kw_jahr: zahl(form.leistungspreis_hoch_eur_kw_jahr),
      arbeitspreis_hoch_ct_kwh: zahl(form.arbeitspreis_hoch_ct_kwh),
      benutzungsstunden_grenze: zahl(form.benutzungsstunden_grenze),
      messstellenbetrieb_eur_jahr: zahl(form.messstellenbetrieb_eur_jahr),
      quelle: form.quelle.trim() || null,
    });
  };

  return (
    <form onSubmit={absenden} className="space-y-5">
      {(error || lokalerFehler) && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertCircle size={16} className="text-red-600 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{lokalerFehler || error}</p>
        </div>
      )}

      <div>
        <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Stammdaten</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Feld label="Netzbetreiber *">
            <input
              value={form.netzbetreiber}
              onChange={set('netzbetreiber')}
              className={eingabeKlasse}
              placeholder="Name laut Preisblatt"
              autoFocus
            />
          </Feld>
          <Feld label="Spannungsebene">
            <select value={form.spannungsebene} onChange={set('spannungsebene')} className={eingabeKlasse}>
              {SPANNUNGSEBENEN.map((e) => <option key={e.wert} value={e.wert}>{e.label}</option>)}
            </select>
          </Feld>
          <Feld label="Gueltig ab *">
            <input type="date" value={form.gueltig_ab || ''} onChange={set('gueltig_ab')} className={eingabeKlasse} />
          </Feld>
          <Feld label="Gueltig bis" hinweis="leer = unbefristet">
            <input type="date" value={form.gueltig_bis || ''} onChange={set('gueltig_bis')} className={eingabeKlasse} />
          </Feld>
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">SLP — ohne Leistungsmessung</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Feld label="Arbeitspreis" einheit="ct/kWh">
            <input type="number" step="0.0001" value={form.arbeitspreis_ct_kwh} onChange={set('arbeitspreis_ct_kwh')} className={eingabeKlasse} />
          </Feld>
          <Feld label="Grundpreis" einheit="EUR/Jahr">
            <input type="number" step="0.01" value={form.grundpreis_eur_jahr} onChange={set('grundpreis_eur_jahr')} className={eingabeKlasse} />
          </Feld>
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">RLM — unter der Benutzungsstundengrenze</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Feld label="Leistungspreis" einheit="EUR/kW/Jahr">
            <input type="number" step="0.01" value={form.leistungspreis_eur_kw_jahr} onChange={set('leistungspreis_eur_kw_jahr')} className={eingabeKlasse} />
          </Feld>
          <Feld label="Arbeitspreis" einheit="ct/kWh">
            <input type="number" step="0.0001" value={form.arbeitspreis_rlm_ct_kwh} onChange={set('arbeitspreis_rlm_ct_kwh')} className={eingabeKlasse} />
          </Feld>
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">RLM — ab der Benutzungsstundengrenze (Hochlast)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Feld label="Leistungspreis" einheit="EUR/kW/Jahr">
            <input type="number" step="0.01" value={form.leistungspreis_hoch_eur_kw_jahr} onChange={set('leistungspreis_hoch_eur_kw_jahr')} className={eingabeKlasse} />
          </Feld>
          <Feld label="Arbeitspreis" einheit="ct/kWh">
            <input type="number" step="0.0001" value={form.arbeitspreis_hoch_ct_kwh} onChange={set('arbeitspreis_hoch_ct_kwh')} className={eingabeKlasse} />
          </Feld>
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Sonstiges</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Feld label="Benutzungsstundengrenze" einheit="h/Jahr" hinweis="Verbrauch / Jahreshoechstleistung; ueblich 2.500">
            <input type="number" step="1" value={form.benutzungsstunden_grenze} onChange={set('benutzungsstunden_grenze')} className={eingabeKlasse} />
          </Feld>
          <Feld label="Messstellenbetrieb" einheit="EUR/Jahr">
            <input type="number" step="0.01" value={form.messstellenbetrieb_eur_jahr} onChange={set('messstellenbetrieb_eur_jahr')} className={eingabeKlasse} />
          </Feld>
          <div className="sm:col-span-2">
            <Feld label="Quelle" hinweis="Woher stammt der Wert? Ohne Quelle laesst sich der Preis spaeter nicht pruefen.">
              <input value={form.quelle} onChange={set('quelle')} className={eingabeKlasse} placeholder="Preisblatt 2027, Stand 01.01.2027" />
            </Feld>
          </div>
        </div>
      </div>

      {rlmUnvollstaendig && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <Info size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800">
            Es fehlen RLM-Werte. Fuer RLM-Kunden, deren Fall nicht abgedeckt ist, faellt die Kalkulation
            auf die regionale Schaetzung zurueck und weist das als Schaetzung aus.
          </p>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>Abbrechen</Button>
        <Button type="submit" loading={saving}>Speichern</Button>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// CSV-Import
// ---------------------------------------------------------------------------

const CsvImport = ({ onImport, importing, importFehler, ergebnis, onClose, onReset }) => {
  const [text, setText] = useState('');
  const [dateiHinweis, setDateiHinweis] = useState('');
  const [dateiFehler, setDateiFehler] = useState('');
  const [kopiert, setKopiert] = useState(false);
  const [kopierFehler, setKopierFehler] = useState('');

  const analyse = useMemo(() => parseCsv(text), [text]);
  const sendbar = analyse ? analyse.zeilen.filter((z) => z.fehler.length === 0) : [];
  const fehlerhaft = analyse ? analyse.zeilen.filter((z) => z.fehler.length > 0) : [];

  const dateiWaehlen = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setDateiFehler('');
    setDateiHinweis('');
    onReset();
    try {
      const buffer = await file.arrayBuffer();
      let inhalt = new TextDecoder('utf-8').decode(buffer);
      if (inhalt.includes('\uFFFD')) {
        // Deutsche Excel-Exporte sind haeufig Windows-1252, nicht UTF-8.
        try {
          inhalt = new TextDecoder('windows-1252').decode(buffer);
          setDateiHinweis(`"${file.name}" war nicht UTF-8 und wurde als Windows-1252 gelesen. Bitte Umlaute in der Vorschau pruefen.`);
        } catch {
          setDateiHinweis(`"${file.name}" enthaelt Zeichen, die sich nicht sicher lesen lassen. Bitte als CSV UTF-8 exportieren.`);
        }
      }
      setText(inhalt);
    } catch (err) {
      setDateiFehler(`Datei konnte nicht gelesen werden: ${err.message}`);
    }
  };

  const vorlageKopieren = async () => {
    setKopierFehler('');
    try {
      await navigator.clipboard.writeText(VORLAGE_KOPFZEILE);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2500);
    } catch {
      setKopierFehler('Der Browser hat das Kopieren abgelehnt — bitte die Kopfzeile unten markieren und von Hand kopieren.');
    }
  };

  return (
    <div className="space-y-5">
      {/* Mustervorlage */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-gray-900">Erwartete Kopfzeile</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Erste Zeile = Spaltennamen. Semikolon oder Komma als Trennzeichen. Spalten duerfen fehlen
              oder in anderer Reihenfolge stehen; unbekannte Spalten werden ignoriert.
            </p>
          </div>
          <Button size="sm" variant="secondary" icon={kopiert ? Check : Copy} onClick={vorlageKopieren}>
            {kopiert ? 'Kopiert' : 'Kopfzeile kopieren'}
          </Button>
        </div>
        <pre className="mt-3 text-xs bg-white border border-gray-200 rounded p-2 overflow-x-auto text-gray-700 select-all">{VORLAGE_KOPFZEILE}</pre>
        {kopierFehler && <p className="text-xs text-amber-700 mt-2">{kopierFehler}</p>}

        <details className="mt-3">
          <summary className="text-xs text-gray-600 cursor-pointer select-none">Bedeutung der Spalten</summary>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-1 pr-3">Spalte</th>
                  <th className="py-1 pr-3">Bedeutung</th>
                  <th className="py-1 pr-3">Einheit</th>
                  <th className="py-1">Hinweis</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {CSV_SPALTEN.map((s) => (
                  <tr key={s.key}>
                    <td className="py-1 pr-3 font-mono text-gray-800 whitespace-nowrap">
                      {s.key}{s.pflicht && <span className="text-red-600"> *</span>}
                    </td>
                    <td className="py-1 pr-3 text-gray-700 whitespace-nowrap">{s.label}</td>
                    <td className="py-1 pr-3 text-gray-500 whitespace-nowrap">{s.einheit || '–'}</td>
                    <td className="py-1 text-gray-500">{s.hinweis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-gray-500 mt-2">
              * Pflichtfeld. Dezimaltrennzeichen: Komma oder Punkt. Datum: JJJJ-MM-TT oder TT.MM.JJJJ.
              Bestehende Preisblaetter werden ueber (Netzbetreiber, Spannungsebene, Gueltig ab) erkannt und aktualisiert.
            </p>
          </div>
        </details>
      </div>

      {/* Eingabe */}
      <div>
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <label className="block text-sm font-medium text-gray-700">CSV einfuegen</label>
          <label className="inline-flex items-center gap-2 text-sm text-primary hover:underline cursor-pointer">
            <FileSpreadsheet size={14} />
            <span>oder Datei waehlen</span>
            <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={dateiWaehlen} className="hidden" />
          </label>
        </div>
        <textarea
          value={text}
          onChange={(e) => { setText(e.target.value); setDateiHinweis(''); setDateiFehler(''); onReset(); }}
          rows={7}
          spellCheck={false}
          placeholder={`${VORLAGE_KOPFZEILE}\n…`}
          className={`${eingabeKlasse} font-mono text-xs`}
        />
        {dateiHinweis && <p className="text-xs text-amber-700 mt-1">{dateiHinweis}</p>}
        {dateiFehler && <p className="text-xs text-red-700 mt-1">{dateiFehler}</p>}
      </div>

      {/* Vorschau */}
      {analyse && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <p className="text-sm font-semibold text-gray-900">
              Vorschau: {analyse.zeilen.length} {analyse.zeilen.length === 1 ? 'Zeile' : 'Zeilen'} erkannt
              {fehlerhaft.length > 0 && <span className="text-red-700"> · {fehlerhaft.length} nicht importierbar</span>}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Trennzeichen: {trennerName(analyse.trenner)} · erkannte Spalten:{' '}
              {analyse.kopfKeys.filter(Boolean).length} von {analyse.kopfRoh.length}
            </p>
            {analyse.fehlendePflicht.length > 0 && (
              <p className="text-xs text-red-700 mt-1">
                Pflichtspalte fehlt in der Kopfzeile: {analyse.fehlendePflicht.join(', ')}
              </p>
            )}
            {analyse.unbekannteSpalten.length > 0 && (
              <p className="text-xs text-amber-700 mt-1">
                Ignorierte Spalten: {analyse.unbekannteSpalten.join(', ')}
              </p>
            )}
          </div>

          <div className="overflow-x-auto max-h-72">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="px-2 py-2">CSV-Zeile</th>
                  <th className="px-2 py-2">Netzbetreiber</th>
                  <th className="px-2 py-2">Ebene</th>
                  <th className="px-2 py-2">Gueltig ab</th>
                  <th className="px-2 py-2 text-right">SLP AP</th>
                  <th className="px-2 py-2 text-right">LP</th>
                  <th className="px-2 py-2 text-right">RLM AP</th>
                  <th className="px-2 py-2 text-right">LP hoch</th>
                  <th className="px-2 py-2 text-right">AP hoch</th>
                  <th className="px-2 py-2 text-right">Grenze</th>
                  <th className="px-2 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {analyse.zeilen.map((z) => {
                  const schlecht = z.fehler.length > 0;
                  return (
                    <tr key={z.csvZeile} className={schlecht ? 'bg-red-50' : z.warnungen.length ? 'bg-amber-50/60' : ''}>
                      <td className="px-2 py-1.5 text-gray-500">{z.csvZeile}</td>
                      <td className="px-2 py-1.5 text-gray-900">{z.daten.netzbetreiber || '–'}</td>
                      <td className="px-2 py-1.5 text-gray-700">{z.daten.spannungsebene || 'NS'}</td>
                      <td className="px-2 py-1.5 text-gray-700">{z.daten.gueltig_ab || '–'}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700">{fmtZahl(z.daten.arbeitspreis_ct_kwh, 2, 4)}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700">{fmtZahl(z.daten.leistungspreis_eur_kw_jahr)}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700">{fmtZahl(z.daten.arbeitspreis_rlm_ct_kwh, 2, 4)}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700">{fmtZahl(z.daten.leistungspreis_hoch_eur_kw_jahr)}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700">{fmtZahl(z.daten.arbeitspreis_hoch_ct_kwh, 2, 4)}</td>
                      <td className="px-2 py-1.5 text-right text-gray-700">{fmtGanz(z.daten.benutzungsstunden_grenze)}</td>
                      <td className="px-2 py-1.5">
                        {schlecht ? (
                          <span className="text-red-700">{z.fehler.join(' · ')}</span>
                        ) : z.warnungen.length ? (
                          <span className="text-amber-700">{z.warnungen.join(' · ')}</span>
                        ) : (
                          <span className="text-green-700">wird importiert</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Schreibfehler des Imports */}
      {importFehler && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <AlertCircle size={16} className="text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-red-800">Import wurde nicht ausgefuehrt</p>
            <p className="text-sm text-red-700 mt-0.5">{importFehler}</p>
          </div>
        </div>
      )}

      {/* Ergebnis — erst nachdem der Server geantwortet hat */}
      {ergebnis && (
        <div className={`border rounded-lg p-4 ${ergebnis.fehler.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
          <p className={`text-sm font-semibold ${ergebnis.fehler.length > 0 ? 'text-amber-900' : 'text-green-900'}`}>
            Server-Antwort: {ergebnis.angelegt} angelegt, {ergebnis.aktualisiert} aktualisiert
            {ergebnis.fehler.length > 0 && `, ${ergebnis.fehler.length} abgelehnt`}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            {ergebnis.nichtGesendet > 0
              ? `${ergebnis.nichtGesendet} Zeile(n) wurden gar nicht gesendet, weil sie schon lokal fehlerhaft waren.`
              : 'Alle vorbereiteten Zeilen wurden gesendet.'}
          </p>
          {ergebnis.fehler.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-amber-900 max-h-40 overflow-y-auto">
              {ergebnis.fehler.map((f, i) => (
                <li key={i}>
                  <span className="font-mono">Zeile {String(f.zeile ?? '?')}</span>: {f.grund || 'ohne Begruendung'}
                </li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-gray-500 mt-2">
            Die Zeilennummer bezieht sich auf die gesendeten Datensaetze in der Reihenfolge der Vorschau
            (ohne die lokal abgewiesenen Zeilen).
          </p>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          {sendbar.length > 0
            ? `${sendbar.length} von ${analyse ? analyse.zeilen.length : 0} Zeilen werden gesendet.`
            : 'Noch nichts zum Senden.'}
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={importing}>Schliessen</Button>
          <Button
            type="button"
            icon={Upload}
            loading={importing}
            disabled={sendbar.length === 0}
            onClick={() => onImport(sendbar.map((z) => z.daten), fehlerhaft.length)}
          >
            {sendbar.length} Zeilen importieren
          </Button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------

const status = (p) => {
  const heute = heuteISO();
  if (p.gueltig_ab && p.gueltig_ab > heute) return 'geplant';
  if (p.gueltig_bis && p.gueltig_bis < heute) return 'abgelaufen';
  return 'aktiv';
};

const STATUS_META = {
  aktiv: { label: 'Aktiv', cls: 'bg-green-100 text-green-700' },
  geplant: { label: 'Geplant', cls: 'bg-blue-100 text-blue-700' },
  abgelaufen: { label: 'Abgelaufen', cls: 'bg-red-100 text-red-700' },
};

const normalisiere = (p) => ({
  ...p,
  spannungsebene: p.spannungsebene || 'NS',
  gueltig_ab: nurTag(p.gueltig_ab),
  gueltig_bis: nurTag(p.gueltig_bis),
  arbeitspreis_ct_kwh: zuZahl(p.arbeitspreis_ct_kwh),
  grundpreis_eur_jahr: zuZahl(p.grundpreis_eur_jahr),
  leistungspreis_eur_kw_jahr: zuZahl(p.leistungspreis_eur_kw_jahr),
  arbeitspreis_rlm_ct_kwh: zuZahl(p.arbeitspreis_rlm_ct_kwh),
  leistungspreis_hoch_eur_kw_jahr: zuZahl(p.leistungspreis_hoch_eur_kw_jahr),
  arbeitspreis_hoch_ct_kwh: zuZahl(p.arbeitspreis_hoch_ct_kwh),
  benutzungsstunden_grenze: zuZahl(p.benutzungsstunden_grenze),
  messstellenbetrieb_eur_jahr: zuZahl(p.messstellenbetrieb_eur_jahr),
});

const rlmKomplett = (p) => p.leistungspreis_eur_kw_jahr != null && p.arbeitspreis_rlm_ct_kwh != null
  && p.leistungspreis_hoch_eur_kw_jahr != null && p.arbeitspreis_hoch_ct_kwh != null;

const Netzentgelte = () => {
  const [preisblaetter, setPreisblaetter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');   // Liste ist nicht da
  const [writeError, setWriteError] = useState(''); // Liste bleibt gueltig
  const [erfolg, setErfolg] = useState('');

  const [formOffen, setFormOffen] = useState(false);
  const [bearbeitet, setBearbeitet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [importOffen, setImportOffen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFehler, setImportFehler] = useState('');
  const [importErgebnis, setImportErgebnis] = useState(null);

  const [suche, setSuche] = useState('');
  const [ebeneFilter, setEbeneFilter] = useState('alle');
  const [nurAktive, setNurAktive] = useState(false);

  const laden = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiFetch('/api/netzentgelte');
      if (!resp.ok) {
        setLoadError(await leseFehler(resp, 'Preisblaetter konnten nicht geladen werden'));
        setPreisblaetter([]);
        return;
      }
      const data = await resp.json();
      setPreisblaetter(Array.isArray(data) ? data.map(normalisiere) : []);
      setLoadError('');
    } catch (err) {
      setLoadError(`Server nicht erreichbar: ${err.message}`);
      setPreisblaetter([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const zeilen = useMemo(() => [...preisblaetter]
    .map((p) => ({ ...p, _status: status(p) }))
    .sort((a, b) => (a.netzbetreiber || '').localeCompare(b.netzbetreiber || '', 'de')
      || (a.spannungsebene || '').localeCompare(b.spannungsebene || '')
      || String(b.gueltig_ab || '').localeCompare(String(a.gueltig_ab || ''))),
  [preisblaetter]);

  const kennzahlen = useMemo(() => ({
    gesamt: zeilen.length,
    netzbetreiber: new Set(zeilen.map((z) => z.netzbetreiber)).size,
    aktiv: zeilen.filter((z) => z._status === 'aktiv').length,
    mitRlm: zeilen.filter(rlmKomplett).length,
  }), [zeilen]);

  const sichtbar = zeilen.filter((z) => {
    if (ebeneFilter !== 'alle' && z.spannungsebene !== ebeneFilter) return false;
    if (nurAktive && z._status !== 'aktiv') return false;
    if (suche) {
      const q = suche.toLowerCase();
      if (!`${z.netzbetreiber || ''} ${z.quelle || ''}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const speichern = async (daten) => {
    setSaving(true);
    setFormError('');
    try {
      const resp = bearbeitet
        ? await apiFetch(`/api/netzentgelte/${bearbeitet.id}`, { method: 'PUT', body: JSON.stringify(daten) })
        : await apiFetch('/api/netzentgelte', { method: 'POST', body: JSON.stringify(daten) });
      if (!resp.ok) {
        setFormError(await leseFehler(resp, 'Preisblatt konnte nicht gespeichert werden'));
        return;
      }
      // Erst jetzt, nach der Bestaetigung des Servers, gilt es als gespeichert.
      setFormOffen(false);
      setBearbeitet(null);
      setErfolg(bearbeitet ? 'Preisblatt aktualisiert.' : 'Preisblatt angelegt.');
      await laden();
      // Auch den Modul-Cache erneuern, aus dem die Rechen-Engine synchron liest —
      // sonst rechnet der Kalkulator mit dem alten Preisblatt weiter.
      await ladeNetzentgelte({ force: true });
    } catch (err) {
      setFormError(`Server nicht erreichbar: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const loeschen = async (p) => {
    const frage = `Preisblatt "${p.netzbetreiber}" (${p.spannungsebene}, ab ${formatDate(p.gueltig_ab)}) loeschen?\n\n`
      + 'Danach rechnet die Kalkulation fuer diesen Netzbetreiber wieder mit der regionalen Schaetzung.';
    if (!window.confirm(frage)) return;
    setWriteError('');
    setErfolg('');
    try {
      const resp = await apiFetch(`/api/netzentgelte/${p.id}`, { method: 'DELETE' });
      if (!resp.ok) {
        setWriteError(await leseFehler(resp, 'Preisblatt konnte nicht geloescht werden'));
        return;
      }
      setErfolg('Preisblatt geloescht.');
      await laden();
      // Auch den Modul-Cache erneuern, aus dem die Rechen-Engine synchron liest —
      // sonst rechnet der Kalkulator mit dem alten Preisblatt weiter.
      await ladeNetzentgelte({ force: true });
    } catch (err) {
      setWriteError(`Server nicht erreichbar: ${err.message}`);
    }
  };

  const importieren = async (rows, nichtGesendet) => {
    setImporting(true);
    setImportFehler('');
    setImportErgebnis(null);
    try {
      const resp = await apiFetch('/api/netzentgelte/import', {
        method: 'POST',
        body: JSON.stringify({ rows }),
      });
      if (!resp.ok) {
        setImportFehler(await leseFehler(resp, 'Import fehlgeschlagen'));
        return;
      }
      const data = await resp.json();
      setImportErgebnis({
        angelegt: Number(data?.angelegt) || 0,
        aktualisiert: Number(data?.aktualisiert) || 0,
        fehler: Array.isArray(data?.fehler) ? data.fehler : [],
        nichtGesendet,
      });
      await laden();
      // Auch den Modul-Cache erneuern, aus dem die Rechen-Engine synchron liest —
      // sonst rechnet der Kalkulator mit dem alten Preisblatt weiter.
      await ladeNetzentgelte({ force: true });
    } catch (err) {
      setImportFehler(`Server nicht erreichbar: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const formularWerte = bearbeitet ? {
    netzbetreiber: bearbeitet.netzbetreiber || '',
    spannungsebene: bearbeitet.spannungsebene || 'NS',
    gueltig_ab: bearbeitet.gueltig_ab || '',
    gueltig_bis: bearbeitet.gueltig_bis || '',
    arbeitspreis_ct_kwh: bearbeitet.arbeitspreis_ct_kwh ?? '',
    grundpreis_eur_jahr: bearbeitet.grundpreis_eur_jahr ?? '',
    leistungspreis_eur_kw_jahr: bearbeitet.leistungspreis_eur_kw_jahr ?? '',
    arbeitspreis_rlm_ct_kwh: bearbeitet.arbeitspreis_rlm_ct_kwh ?? '',
    leistungspreis_hoch_eur_kw_jahr: bearbeitet.leistungspreis_hoch_eur_kw_jahr ?? '',
    arbeitspreis_hoch_ct_kwh: bearbeitet.arbeitspreis_hoch_ct_kwh ?? '',
    benutzungsstunden_grenze: bearbeitet.benutzungsstunden_grenze ?? '',
    messstellenbetrieb_eur_jahr: bearbeitet.messstellenbetrieb_eur_jahr ?? '',
    quelle: bearbeitet.quelle || '',
  } : LEERES_FORMULAR;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Netzentgelte</h1>
          <p className="text-gray-500 mt-1">
            {loading
              ? 'Lade Preisblaetter …'
              : loadError
                ? 'Preisblaetter konnten nicht geladen werden'
                : `${kennzahlen.gesamt} Preisblaetter von ${kennzahlen.netzbetreiber} Netzbetreibern`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="secondary" icon={RefreshCw} onClick={laden} disabled={loading}>Aktualisieren</Button>
          {/* Bewusst NICHT an writeError gekoppelt — ein fehlgeschlagener
              Schreibversuch darf das Anlegen nicht dauerhaft sperren. */}
          <Button
            variant="secondary"
            icon={Upload}
            onClick={() => { setImportFehler(''); setImportErgebnis(null); setImportOffen(true); }}
            disabled={loading || !!loadError}
          >
            CSV importieren
          </Button>
          <Button
            icon={Plus}
            onClick={() => { setBearbeitet(null); setFormError(''); setFormOffen(true); }}
            disabled={loading || !!loadError}
          >
            Preisblatt anlegen
          </Button>
        </div>
      </div>

      {/* Ladefehler: blendet die Liste aus. */}
      {loadError && (
        <Card className="bg-red-50 border-red-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-red-600 mt-0.5 shrink-0" size={18} />
            <div>
              <p className="font-semibold text-red-800">Preisblaetter konnten nicht geladen werden</p>
              <p className="text-sm text-red-700 mt-1">{loadError}</p>
              <p className="text-sm text-red-700 mt-1">
                Solange dieser Fehler steht, ist unbekannt, welche Preisblaetter hinterlegt sind —
                das ist nicht dasselbe wie &bdquo;es gibt keine&ldquo;.
              </p>
              <Button size="sm" className="mt-3" onClick={laden}>Erneut versuchen</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Schreibfehler: die Liste unten bleibt gueltig und bedienbar. */}
      {writeError && (
        <Card className="bg-red-50 border-red-200">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-red-800">Aktion wurde nicht ausgefuehrt</p>
              <p className="text-sm text-red-700 mt-0.5">{writeError}</p>
            </div>
            <button onClick={() => setWriteError('')} className="p-1 text-red-400 hover:text-red-700 rounded" title="Hinweis ausblenden">
              <X size={16} />
            </button>
          </div>
        </Card>
      )}

      {erfolg && (
        <Card className="bg-green-50 border-green-200">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <CircleCheck size={16} className="text-green-600 shrink-0" />
              <p className="text-sm text-green-800">{erfolg}</p>
            </div>
            <button onClick={() => setErfolg('')} className="p-1 text-green-500 hover:text-green-800 rounded" title="Hinweis ausblenden">
              <X size={16} />
            </button>
          </div>
        </Card>
      )}

      {/* Kennzahlen — bei Ladefehler "–", nie "0". */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Preisblaetter', value: kennzahlen.gesamt, icon: FileSpreadsheet },
          { label: 'Netzbetreiber', value: kennzahlen.netzbetreiber, icon: Network },
          { label: 'Heute gueltig', value: kennzahlen.aktiv, icon: CircleCheck },
          { label: 'Mit vollstaendigen RLM-Werten', value: kennzahlen.mitRlm, icon: Gauge },
        ].map((k) => (
          <div key={k.label} className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-2 text-gray-500">
              <k.icon size={15} />
              <span className="text-sm">{k.label}</span>
            </div>
            <p className="text-2xl font-bold mt-1 text-gray-900">{loading || loadError ? '–' : k.value}</p>
          </div>
        ))}
      </div>

      {/* Erklaertext */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Info size={18} className="text-blue-600 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-900 space-y-2">
            <p className="font-semibold">Warum diese Seite gepflegt werden muss</p>
            <p>
              Das Netzentgelt zahlt der Kunde an seinen oertlichen Netzbetreiber. Es ist regional
              sehr unterschiedlich und steht im jaehrlich neu veroeffentlichten Preisblatt des
              Netzbetreibers.
            </p>
            <p>
              Ist fuer einen Netzbetreiber <strong>kein Preisblatt hinterlegt</strong>, rechnet die
              Kalkulation mit einer <strong>regionalen Schaetzung nach zweistelliger Postleitzahl</strong>.
              Diese Schaetzung ist ein Regionaldurchschnitt: sie unterscheidet nicht zwischen mehreren
              Netzbetreibern derselben Region und enthaelt fuer RLM-Kunden keinen Leistungspreis.
              <strong> Fuer verbindliche Angebote reicht sie nicht aus</strong> — sie ist ein Anhaltswert
              fuer die erste Einschaetzung.
            </p>
            <p>
              Jede Kalkulation weist deshalb aus, woher ihr Netzentgelt stammt: <em>Preisblatt</em> oder
              <em> Schaetzung</em>. Netzbetreiber veroeffentlichen ihre Preisblaetter jaehrlich neu;
              die Werte fuer das Folgejahr stehen ueblicherweise zum Jahreswechsel fest. Preisblaetter
              gehoeren also einmal im Jahr geprueft und mit neuem <em>Gueltig ab</em> nachgetragen.
            </p>
          </div>
        </div>
      </Card>

      {/* Tabelle */}
      <Card padding={false}>
        <div className="p-4 border-b border-gray-200 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="Netzbetreiber oder Quelle suchen …"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            />
          </div>
          <select
            value={ebeneFilter}
            onChange={(e) => setEbeneFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
          >
            <option value="alle">Alle Spannungsebenen</option>
            {SPANNUNGSEBENEN.map((e) => <option key={e.wert} value={e.wert}>{e.label}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={nurAktive} onChange={(e) => setNurAktive(e.target.checked)} className="rounded border-gray-300" />
            nur heute gueltige
          </label>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <p className="text-sm text-gray-400 py-12 text-center">Lade Preisblaetter …</p>
          ) : sichtbar.length === 0 ? (
            <div className="py-12 text-center">
              <Network className="mx-auto text-gray-300 mb-3" size={32} />
              <p className="text-gray-600 font-medium">
                {loadError
                  ? 'Keine Daten verfuegbar'
                  : kennzahlen.gesamt === 0
                    ? 'Noch kein Preisblatt hinterlegt'
                    : 'Kein Preisblatt passt zu den Filtern'}
              </p>
              {!loadError && kennzahlen.gesamt === 0 && (
                <>
                  <p className="text-sm text-gray-500 mt-1">
                    Bis dahin rechnet jede Kalkulation mit der regionalen Schaetzung und weist sie als solche aus.
                  </p>
                  <div className="flex justify-center gap-2 mt-3">
                    <Button icon={Plus} onClick={() => { setBearbeitet(null); setFormError(''); setFormOffen(true); }}>
                      Erstes Preisblatt anlegen
                    </Button>
                    <Button variant="secondary" icon={Upload} onClick={() => { setImportFehler(''); setImportErgebnis(null); setImportOffen(true); }}>
                      CSV importieren
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 bg-gray-50">
                  <th className="px-4 py-2" rowSpan={2}>Netzbetreiber</th>
                  <th className="px-3 py-2" rowSpan={2}>Ebene</th>
                  <th className="px-3 py-2" rowSpan={2}>Gueltig</th>
                  <th className="px-3 py-2 text-center border-l border-gray-200" colSpan={2}>SLP</th>
                  <th className="px-3 py-2 text-center border-l border-gray-200" colSpan={2}>RLM unter Grenze</th>
                  <th className="px-3 py-2 text-center border-l border-gray-200" colSpan={2}>RLM ab Grenze</th>
                  <th className="px-3 py-2 text-right border-l border-gray-200" rowSpan={2}>Grenze<br /><span className="normal-case font-normal">h/a</span></th>
                  <th className="px-3 py-2 text-right" rowSpan={2}>MSB<br /><span className="normal-case font-normal">EUR/a</span></th>
                  <th className="px-3 py-2" rowSpan={2}>Quelle</th>
                  <th className="px-3 py-2" rowSpan={2}></th>
                </tr>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 bg-gray-50">
                  <th className="px-3 pb-2 text-right border-l border-gray-200 font-normal normal-case">AP ct/kWh</th>
                  <th className="px-3 pb-2 text-right font-normal normal-case">GP EUR/a</th>
                  <th className="px-3 pb-2 text-right border-l border-gray-200 font-normal normal-case">LP EUR/kW/a</th>
                  <th className="px-3 pb-2 text-right font-normal normal-case">AP ct/kWh</th>
                  <th className="px-3 pb-2 text-right border-l border-gray-200 font-normal normal-case">LP EUR/kW/a</th>
                  <th className="px-3 pb-2 text-right font-normal normal-case">AP ct/kWh</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sichtbar.map((p) => {
                  const st = STATUS_META[p._status];
                  const luecke = !rlmKomplett(p);
                  return (
                    <tr key={p.id} className="hover:bg-gray-50 align-top">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{p.netzbetreiber}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${st.cls}`}>{st.label}</span>
                          {luecke && (
                            <span
                              className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800"
                              title="Es fehlen Leistungs- oder Arbeitspreise fuer RLM. In diesen Faellen faellt die Kalkulation auf die regionale Schaetzung zurueck."
                            >
                              RLM unvollstaendig
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-gray-700">{p.spannungsebene}</td>
                      <td className="px-3 py-3 text-gray-600 whitespace-nowrap">
                        {formatDate(p.gueltig_ab)}
                        <br />
                        <span className="text-xs text-gray-400">
                          bis {p.gueltig_bis ? formatDate(p.gueltig_bis) : 'unbefristet'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right text-gray-900 border-l border-gray-100">{fmtZahl(p.arbeitspreis_ct_kwh, 2, 4)}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{fmtZahl(p.grundpreis_eur_jahr)}</td>
                      <td className="px-3 py-3 text-right text-gray-900 border-l border-gray-100">{fmtZahl(p.leistungspreis_eur_kw_jahr)}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{fmtZahl(p.arbeitspreis_rlm_ct_kwh, 2, 4)}</td>
                      <td className="px-3 py-3 text-right text-gray-900 border-l border-gray-100">{fmtZahl(p.leistungspreis_hoch_eur_kw_jahr)}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{fmtZahl(p.arbeitspreis_hoch_ct_kwh, 2, 4)}</td>
                      <td className="px-3 py-3 text-right text-gray-700 border-l border-gray-100">{fmtGanz(p.benutzungsstunden_grenze)}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{fmtZahl(p.messstellenbetrieb_eur_jahr)}</td>
                      <td className="px-3 py-3 text-gray-500 max-w-[200px]">
                        {p.quelle || <span className="text-amber-700">ohne Quellenangabe</span>}
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => { setBearbeitet(p); setFormError(''); setFormOffen(true); }}
                          className="p-1.5 text-gray-400 hover:text-primary rounded hover:bg-gray-100"
                          title="Preisblatt bearbeiten"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => loeschen(p)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
                          title="Preisblatt loeschen"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {/* Wie gerechnet wird */}
      <Card>
        <Card.Title>Wie aus dem Preisblatt ein Netzentgelt wird</Card.Title>
        <div className="text-sm text-gray-500 mt-2 space-y-2">
          <p>
            <strong className="text-gray-700">SLP</strong> (ohne Leistungsmessung):
            Arbeitspreis mal Verbrauch, plus Grundpreis und Messstellenbetrieb.
          </p>
          <p>
            <strong className="text-gray-700">RLM</strong> (mit registrierender Leistungsmessung, praktisch
            ab rund 100.000 kWh): zweiteiliger Tarif aus Leistungspreis mal Jahreshoechstleistung plus
            Arbeitspreis mal Verbrauch, dazu der Messstellenbetrieb. Welcher der beiden RLM-Tarife gilt,
            entscheiden die Benutzungsstunden (Jahresverbrauch geteilt durch Jahreshoechstleistung):
            ab der hinterlegten Grenze — ueblich 2.500 h/a — gilt der Hochlast-Tarif mit hohem
            Leistungspreis und niedrigem Arbeitspreis, darunter der andere.
          </p>
          <p>
            Fehlen die RLM-Werte im Preisblatt, faellt die Kalkulation fuer RLM-Kunden auf die regionale
            Schaetzung zurueck und kennzeichnet das Ergebnis entsprechend.
          </p>
        </div>
      </Card>

      <Modal
        isOpen={formOffen}
        onClose={() => { setFormOffen(false); setBearbeitet(null); }}
        title={bearbeitet ? 'Preisblatt bearbeiten' : 'Preisblatt anlegen'}
        size="xl"
      >
        <PreisblattForm
          key={bearbeitet ? `edit-${bearbeitet.id}` : 'neu'}
          initial={formularWerte}
          onSave={speichern}
          onCancel={() => { setFormOffen(false); setBearbeitet(null); }}
          saving={saving}
          error={formError}
        />
      </Modal>

      <Modal isOpen={importOffen} onClose={() => setImportOffen(false)} title="Preisblaetter aus CSV importieren" size="xl">
        <CsvImport
          onImport={importieren}
          importing={importing}
          importFehler={importFehler}
          ergebnis={importErgebnis}
          onClose={() => setImportOffen(false)}
          onReset={() => { setImportErgebnis(null); setImportFehler(''); }}
        />
      </Modal>
    </div>
  );
};

export default Netzentgelte;
